<template>
  <div class="">
    <label
      v-if="label"
      :for="name"
      class="mb-1 block text-left text-xs font-medium text-background-fg/70"
    >
      {{ label }}
    </label>
    <div class="flex flex-wrap items-center gap-2">
      <template v-for="rate in vatOptions" :key="rate">
        <button
          type="button"
          :disabled="disabled"
          :class="[
            inputValue === rate
              ? 'bg-primary text-primary-fg'
              : '',
            'w-16 rounded-md border border-background-fg/6 px-3 py-2 font-semibold',
          ]"
          @click="setVatRate(rate)"
        >
          <span v-if="rate === null" class="text-xs"
            >None</span
          >
          <template v-else>{{ rate }}%</template>
        </button>
      </template>

      <div class="flex items-center gap-2 sm:basis-auto">
        <input
          type="text"
          :name="name"
          :class="[
            'block w-full max-w-24 flex-1 rounded-md border border-background-fg/8 text-base font-semibold focus:outline-none', // Font size set at >14px (text-base) so no zoom on mobile when focused
            hasError
              ? 'border-error text-error placeholder:text-error focus:border-error focus:ring-error'
              : 'focus:border-primary focus:ring-primary',
          ]"
          :value="inputValue"
          :aria-invalid="!!errorMessage"
          :disabled="disabled"
          enterKeyHint="done"
          @input="handleVatTextInput"
          @blur="handleBlur"
          @keydown.backspace="handleBackspace"
        />
        <span class="text-lg font-semibold">%</span>
      </div>
    </div>
    <p
      v-if="hint"
      class="mt-1 text-xs font-medium text-background-fg/50"
    >
      {{ hint }}
    </p>
    <template v-if="errorMessage">
      <p class="mt-1 text-xs font-medium text-error">
        {{ errorMessage }}
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useField } from "vee-validate"
const props = withDefaults(
  defineProps<{
    type?: string
    modelValue?: number | null
    name: string
    label: string
    placeholder?: string
    hasError?: boolean
    disabled?: boolean
    hint?: string
  }>(),
  {
    modelValue: null,
    type: "text",
    placeholder: "",
    hasError: false,
    disabled: false,
    hint: "",
  }
)

const { isUKmerchant } = useTenant()

const vatOptions = computed(() =>
  isUKmerchant.value ? [0, 5, 20] : [0, 13.5, 23]
)

const {
  value: inputValue,
  errorMessage,
  handleBlur,
  handleChange,
} = useField(toRef(props, "name"), undefined, {
  ...(props.modelValue && {
    initialValue: props.modelValue,
  }),
})

const setVatRate = (value: number | null) => {
  handleChange(value)
}

const handleVatTextInput = (event: Event) => {
  const value = (event?.target as HTMLInputElement)?.value
  if (!value) handleChange(null)
  handleChange(Number(value))
}

const handleBackspace = (event: KeyboardEvent) => {
  if (inputValue.value === 0) {
    event.preventDefault()
  }
}
</script>
